import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Components
import { SupervisorPinModal } from "../SuperuserPinModal/SuperuserPinModal";

// Services
import { posService } from "../../../../services/pos.service";
import { toast } from "react-toastify";
import { useAuth } from "../../../auth";

// Types
export interface Table {
  table_id: string;
  table_name: string;
  status: "free" | "occupied" | "closed";
  capacity: number;
  location: {
    zone: string;
  };
}

interface TableSelectorProps {
  onSelectTable: (table: Table) => void;
}

export const TableSelector: React.FC<TableSelectorProps> = ({ onSelectTable }) => {
  const [tables, setTables] = useState<Table[]>([]);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { user } = useAuth();
  const branchindex = user?.assignment_details?.id;
  const [BranchId, setBranchId] = useState('');

  useEffect(() => {
    const fetchBranchAndLoadTables = async () => {
      try {
        setLoading(true);

        // Step 1: Get branch index ID from user
        const branchDocId = user?.assignment_details?.id;
        const store_id = user?.store_id;

        if (!branchDocId || !store_id) {
          console.warn("Missing branchDocId or store_id");
          setTables([]);
          return;
        }

        // Step 2: Fetch branch data to get real branch_id
        const branchResponse = await posService.getBranchById(branchDocId, store_id);
        const branchData = branchResponse?.data;

        if (!branchData) {
          console.error("No branch data returned");
          setTables([]);
          return;
        }

        const { branch_id } = branchData;

        // Step 3: Load tables using branch_id
        const tablesResponse = await posService.getAllTablesForBranch(branch_id);

        if (tablesResponse.success && Array.isArray(tablesResponse.data)) {
          setTables(tablesResponse.data);
        } else {
          console.warn("No tables returned or invalid format");
          setTables([]);
        }
      } catch (error) {
        console.error("Error in branch/table flow:", error);
        setTables([]);
        toast.error("Failed to load tables");
      } finally {
        setLoading(false);
      }
    };

    fetchBranchAndLoadTables();
  }, [user]);

  // Handle table selection
  const handleTableClick = (table: Table) => {
    if (table.status === "free") {
      confirmSelection(table);
    } else {
      // Show supervisor PIN override modal
      setSelectedTable(table);
      setShowPinModal(true);
    }
  };

  const confirmSelection = (table: Table) => {
    setSelectedTable(table);
    onSelectTable(table);
  };

  const handleSubmitPin = (reason: string) => {
    if (selectedTable) {
      confirmSelection(selectedTable);
    }
    setShowPinModal(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "free":
        return "#c8e6c9"; // Green
      case "occupied":
        return "#fff9c4"; // Yellow
      case "closed":
        return "#ffcdd2"; // Red
      default:
        return "#eeeeee";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Select Table
      </Typography>

      {/* Loading Spinner */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Table Grid */}
          <Grid container spacing={2}>
            {tables.map((table) => (
              <Grid item xs={6} sm={4} md={3} key={table.table_id}>
                <Paper
                  elevation={1}
                  onClick={() => handleTableClick(table)}
                  sx={{
                    height: 100,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: getStatusColor(table.status),
                    cursor: "pointer",
                    border:
                      selectedTable?.table_id === table.table_id
                        ? "3px solid #1E8078"
                        : "1px solid #ccc",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <Typography variant="h6">{table.table_name}</Typography>
                  <Typography variant="body2">
                    {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Selected Table Info */}
          {selectedTable && (
            <Box sx={{ mt: 3 }}>
              <Typography color="text.secondary">Selected Table:</Typography>
              <Typography variant="h6">
                {selectedTable.table_name} ({selectedTable.status})
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* 🔁 Supervisor PIN Modal */}
      <SupervisorPinModal
        open={showPinModal}
        action="override_table_selection"
        reasonLabel="Why are you selecting this table?"
        onClose={() => setShowPinModal(false)}
        onSuccess={handleSubmitPin}
      />
    </Box>
  );
};